/**
 * The site's motion rules (docs/design/MASTER.md section 7), applied once at the app root:
 *
 * 1. No entrance animations on content. Over a hundred framer-motion elements fade or slide in when the page
 *    mounts (initial plus animate) or when they scroll into view (whileInView). The provider below tells every
 *    motion element that is not inside an AnimatePresence to skip its mount animation: it is drawn at its final
 *    state on the first frame. A whileInView reveal is drawn visible too, so scrolling to it changes nothing
 *    (all 44 of them use viewport once, so none fades back out).
 *
 *    This is the same switch framer-motion uses for <AnimatePresence initial={false}>: a PresenceContext with
 *    initial set to false. Elements inside an AnimatePresence get that AnimatePresence's own context, so state
 *    changes keep their motion: menus, modals, sheets, dropdowns and the bottom bar still animate in and out.
 *    Later changes to an element's animate prop (an accordion opening, a value bar moving) are not mount
 *    animations and still run.
 *
 * 2. State changes default to 200ms ease-out (MotionConfig transition), within the 150 to 250ms rule. An element
 *    that sets its own transition keeps it.
 *
 * 3. framer-motion follows the reader's reduced-motion setting (MotionConfig reducedMotion="user"): transforms
 *    are dropped, opacity still fades. The CSS rule in index.css already did this for CSS animations only.
 *
 * Something that must move from the moment it mounts, a loader for example, opts back in with
 * <AllowMountAnimation>.
 */
import type { ReactNode } from "react";
import { MotionConfig, PresenceContext } from "framer-motion";

/** The default for state changes: 200ms, ease-out. */
export const STATE_TRANSITION = { duration: 0.2, ease: [0.22, 1, 0.36, 1] } as const;

// A presence context that blocks mount animations. isPresent stays true, so exit animations never fire from it,
// and register returns a no-op, so nothing waits on these elements.
const NO_MOUNT_ANIMATION = {
  id: "bhg-motion-policy",
  initial: false as const,
  isPresent: true,
  custom: undefined,
  onExitComplete: undefined,
  register: () => () => {},
};

export function MotionPolicy({ children }: { children: ReactNode }) {
  return (
    <MotionConfig reducedMotion="user" transition={STATE_TRANSITION}>
      <PresenceContext.Provider value={NO_MOUNT_ANIMATION}>{children}</PresenceContext.Provider>
    </MotionConfig>
  );
}

/** Lets the motion elements inside animate from mount again. Only for loaders and other progress indicators. */
export function AllowMountAnimation({ children }: { children: ReactNode }) {
  return <PresenceContext.Provider value={null}>{children}</PresenceContext.Provider>;
}
